import {
  Badge,
  Box,
  Checkbox,
  Flex,
  HStack,
  Input,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { UrlLink } from '@resistapp/client/components/links/url-link';
import { theme } from '@resistapp/client/components/shared/theme';
import { Loading } from '@resistapp/client/components/states/loading';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useAdminLabWorkbooks } from '@resistapp/client/hooks/api';
import {
  belongsToCustomerOrganisation,
  belongsToLabOrganisation,
  belongsToResistomapOrProviderOrganisation,
  isProviderAdminUser,
  isResistomapAdminUser,
} from '@resistapp/client/utils/access';
import {
  formatWorkbookTimestamp,
  getLabWorkbookStageHref,
  getLabWorkbookStageLabel,
  getLabWorkbookStatusColor,
} from '@resistapp/client/utils/lab-workbook';
import { AdminLabWorkbookRow } from '@resistapp/common/api-types';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

export function AdminLabWorkbooksView() {
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { user } = useUser();

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (includeCompleted) {
      params.set('includeCompleted', '1');
    }
    if (includeLegacy) {
      params.set('includeLegacy', '1');
    }
    return params.toString();
  }, [includeCompleted, includeLegacy]);

  const { data, isPending, error } = useAdminLabWorkbooks(queryString);

  if (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }

  const workbooks = data?.workbooks ?? [];

  const hideOrganisationColumn = useMemo(() => {
    if (!user) {
      return false;
    }
    const belongsToRestrictedOrganisation = belongsToLabOrganisation(user) || belongsToCustomerOrganisation(user);
    const isPrivilegedAdmin = isResistomapAdminUser(user) || isProviderAdminUser(user);
    return belongsToRestrictedOrganisation && !isPrivilegedAdmin;
  }, [user]);
  const showLinkColumn = useMemo(() => belongsToResistomapOrProviderOrganisation(user), [user]);
  const canEditLabAssignments = useMemo(
    () => Boolean(user && (isResistomapAdminUser(user) || isProviderAdminUser(user))),
    [user],
  );

  const rows = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) {
      return workbooks;
    }

    return workbooks.filter(workbook =>
      getLabWorkbookSearchText(workbook, {
        includeOrganisation: !hideOrganisationColumn,
        includeAssignedLab: canEditLabAssignments,
      }).includes(search),
    );
  }, [canEditLabAssignments, hideOrganisationColumn, searchTerm, workbooks]);

  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Lab Workbooks" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={theme.spacing[3]}
          >
            <Input
              placeholder="Search workbooks"
              aria-label="Search workbooks"
              value={searchTerm}
              onChange={event => {
                setSearchTerm(event.target.value);
              }}
              width={{ base: '100%', md: '360px' }}
            />
            <HStack spacing={theme.spacing[4]}>
              <Checkbox
                isChecked={includeCompleted}
                onChange={event => {
                  setIncludeCompleted(event.currentTarget.checked);
                }}
              >
                Show completed
              </Checkbox>
              <Checkbox
                isChecked={includeLegacy}
                onChange={event => {
                  setIncludeLegacy(event.currentTarget.checked);
                }}
              >
                Show legacy
              </Checkbox>
            </HStack>
          </Flex>
        </Box>

        <Box bg="white" borderRadius={6} padding={theme.spacing[3]} boxShadow="sm">
          {isPending ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Text color="gray.600">No lab workbooks match your filters.</Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Workbook</Th>
                  <Th>Status</Th>
                  {hideOrganisationColumn ? null : <Th>Organisation</Th>}
                  {canEditLabAssignments ? <Th>Assigned lab</Th> : null}
                  {showLinkColumn ? <Th>Links</Th> : null}
                  <Th>Updated</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map(workbook => (
                  <LabWorkbookRow
                    key={workbook.id}
                    workbook={workbook}
                    showOrganisationColumn={!hideOrganisationColumn}
                    showAssignmentColumn={canEditLabAssignments}
                    showLinksColumn={showLinkColumn}
                  />
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>
    </div>
  );
}

function LabWorkbookRow({
  workbook,
  showOrganisationColumn = true,
  showAssignmentColumn = false,
  showLinksColumn = true,
}: {
  workbook: AdminLabWorkbookRow;
  showOrganisationColumn?: boolean;
  showAssignmentColumn?: boolean;
  showLinksColumn?: boolean;
}) {
  const displayName = getLabWorkbookDisplayName(workbook);
  const workbookEntryHref = `/lab/${workbook.id}`;
  const stageHref = getLabWorkbookStageHref(workbook);
  const stageLabel = getLabWorkbookStageLabel(workbook.status);
  const statusColor = getLabWorkbookStatusColor(workbook.status);

  return (
    <Tr>
      <Td>
        <VStack align="flex-start" spacing={0.5}>
          <Link
            as={RouterLink}
            to={workbookEntryHref}
            fontWeight={600}
            color={theme.colors.blue800}
            _hover={{ textDecoration: 'underline' }}
          >
            {displayName}
          </Link>
          <Text fontSize="sm" color="gray.600">
            {workbook.sampleSheetName ?? 'SmartChip spec'}
          </Text>
          {workbook.projectLead ? (
            <Text fontSize="sm" color="gray.600">
              Project lead: {workbook.projectLead}
            </Text>
          ) : null}
        </VStack>
      </Td>
      <Td>
        <Badge
          as={RouterLink}
          to={stageHref}
          colorScheme={statusColor}
          variant="solid"
          textTransform="none"
          paddingX={3}
          paddingY={1}
          _hover={{ textDecoration: 'none', opacity: 0.85 }}
        >
          {stageLabel}
        </Badge>
      </Td>
      {showOrganisationColumn ? (
        <Td>
          <Link
            as={RouterLink}
            to={`/admin/organisation/${workbook.organisation.id}`}
            fontWeight={600}
            color={theme.colors.blue800}
            _hover={{ textDecoration: 'underline' }}
          >
            {workbook.organisation.name}
          </Link>
        </Td>
      ) : null}
      {showAssignmentColumn ? (
        <Td>
          <AssignmentCell workbook={workbook} />
        </Td>
      ) : null}
      {showLinksColumn ? (
        <Td>
          <VStack align="flex-start" spacing={1}>
            {workbook.labSheetUrl ? <UrlLink text="Lab sheet" href={workbook.labSheetUrl} /> : null}
            {workbook.channelUrl ? <UrlLink text="Slack" href={workbook.channelUrl} /> : null}
            {!workbook.labSheetUrl && !workbook.channelUrl ? (
              <Text fontSize="sm" color="gray.500">
                No links
              </Text>
            ) : null}
          </VStack>
        </Td>
      ) : null}
      <Td>
        <Text fontSize="sm" color="gray.600">
          {formatWorkbookTimestamp(workbook.updatedAt)}
        </Text>
      </Td>
    </Tr>
  );
}

function getLabWorkbookDisplayName(workbook: AdminLabWorkbookRow): string {
  return workbook.name?.trim() || workbook.sampleSheetName || 'Untitled workbook';
}

function getLabWorkbookSearchText(
  workbook: AdminLabWorkbookRow,
  {
    includeOrganisation,
    includeAssignedLab,
  }: {
    includeOrganisation: boolean;
    includeAssignedLab: boolean;
  },
): string {
  const fields = [
    getLabWorkbookDisplayName(workbook),
    workbook.sampleSheetName,
    workbook.projectLead,
    getLabWorkbookStageLabel(workbook.status),
    workbook.genePanelName,
    includeOrganisation ? workbook.organisation.name : null,
    includeAssignedLab ? (workbook.assignedLabOrganisation?.name ?? 'Unassigned') : null,
  ];

  return fields
    .filter((field): field is string => Boolean(field))
    .map(field => field.toLowerCase())
    .join(' ');
}

function AssignmentCell({ workbook }: { workbook: AdminLabWorkbookRow }) {
  const assignedLabLabel = workbook.assignedLabOrganisation?.name ?? 'Unassigned';

  return (
    <VStack align="flex-start" spacing={1}>
      <Text fontSize="sm" fontWeight="semibold">
        {assignedLabLabel}
      </Text>
    </VStack>
  );
}
